import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { deleteField, doc, getDoc, setDoc } from "firebase/firestore";
import { useFirestore } from "reactfire";
import { useFeedback } from "../../../providers/feedback/feedback";

const YOUCAT_DESCRIPTION = `YOUCAT, the Youth Catechism of the Catholic Church, presents the faith of the Church in a language that speaks clearly to young people. It helps the youth not only to learn what the Church teaches, but also to understand why the faith matters in daily life, prayer, vocation, service, and witness.

For ICYM Udupi Diocese, the YOUCAT initiative strengthens faith formation among young people by encouraging them to study the teachings of the Church with clarity, conviction, and joy. It becomes a means of forming informed, prayerful, and mission-oriented youth who can live and share the Gospel with confidence.

Through this section, the diocese highlights the importance of YOUCAT as a guide for spiritual growth, leadership, and discipleship, while supporting the efforts of ICYM and YCS coordinators who animate this mission at different levels.`;

type YoucatPageSettings = {
  description: string;
  icymDexcoInchargeName: string;
  icymDexcoInchargeContact: string;
  ycsDexcoInchargeName: string;
  ycsDexcoInchargeContact: string;
  quizEnabled?: boolean;
  quizTitle?: string;
  quizDescription?: string;
  updatedAt?: string;
};

const DEFAULT_SETTINGS: YoucatPageSettings = {
  description: YOUCAT_DESCRIPTION,
  icymDexcoInchargeName: "",
  icymDexcoInchargeContact: "",
  ycsDexcoInchargeName: "",
  ycsDexcoInchargeContact: "",
  quizEnabled: false,
  quizTitle: "YouCat Quiz",
  quizDescription:
    "Login or signup before attending the quiz. The public quiz card appears only when this is enabled.",
};

export const YOUCAT_PAGE_SETTINGS_DOC_ID = "content";
export const YOUCAT_PAGE_SETTINGS_COLLECTION = "youcatPageSettings";

export default function YoucatPageSettingsTab() {
  const firestore = useFirestore();
  const { showSnackbar } = useFeedback();
  const [formValues, setFormValues] = useState<YoucatPageSettings>(
    DEFAULT_SETTINGS,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadSettings() {
      try {
        const snapshot = await getDoc(
          doc(
            firestore,
            YOUCAT_PAGE_SETTINGS_COLLECTION,
            YOUCAT_PAGE_SETTINGS_DOC_ID,
          ),
        );

        if (!isMounted) {
          return;
        }

        if (snapshot.exists()) {
          setFormValues({
            ...DEFAULT_SETTINGS,
            ...(snapshot.data() as Partial<YoucatPageSettings>),
          });
        }
      } catch (error) {
        console.error("Failed to load YouCat page settings", error);
        showSnackbar("Failed to load YouCat page settings");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [firestore, showSnackbar]);

  async function handleSave() {
    try {
      setIsSaving(true);
      await setDoc(
        doc(
          firestore,
          YOUCAT_PAGE_SETTINGS_COLLECTION,
          YOUCAT_PAGE_SETTINGS_DOC_ID,
        ),
        {
          ...formValues,
          description: formValues.description.trim(),
          icymDexcoInchargeName: formValues.icymDexcoInchargeName.trim(),
          icymDexcoInchargeContact: formValues.icymDexcoInchargeContact.trim(),
          ycsDexcoInchargeName: formValues.ycsDexcoInchargeName.trim(),
          ycsDexcoInchargeContact: formValues.ycsDexcoInchargeContact.trim(),
          quizEnabled: Boolean(formValues.quizEnabled),
          quizTitle: String(formValues.quizTitle || "").trim(),
          quizDescription: String(formValues.quizDescription || "").trim(),
          icymDriveFolderId: deleteField(),
          ycsDriveFolderId: deleteField(),
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );
      showSnackbar("YouCat page settings saved successfully");
    } catch (error) {
      console.error("Failed to save YouCat page settings", error);
      showSnackbar("Failed to save YouCat page settings");
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography>Loading YouCat page settings...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            multiline
            minRows={10}
            value={formValues.description}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="ICYM DEXCO YouCat In-charge Name"
            value={formValues.icymDexcoInchargeName}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                icymDexcoInchargeName: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="ICYM DEXCO YouCat In-charge Contact"
            value={formValues.icymDexcoInchargeContact}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                icymDexcoInchargeContact: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="YCS DEXCO YouCat In-charge Name"
            value={formValues.ycsDexcoInchargeName}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                ycsDexcoInchargeName: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="YCS DEXCO YouCat In-charge Contact"
            value={formValues.ycsDexcoInchargeContact}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                ycsDexcoInchargeContact: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(formValues.quizEnabled)}
                onChange={(_event, checked) =>
                  setFormValues((prev) => ({
                    ...prev,
                    quizEnabled: checked,
                  }))
                }
              />
            }
            label="Show YouCat Quiz card on the public page"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Quiz Title"
            value={formValues.quizTitle || ""}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                quizTitle: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Quiz Description"
            value={formValues.quizDescription || ""}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                quizDescription: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save YouCat Page Content"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
